import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { ItineraryItem, DayItinerary } from '../types';
import { parseISO, format, isValid } from 'date-fns';

const toDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp instanceof Date && isValid(timestamp)) return timestamp;
  if (typeof timestamp === 'string') {
    try {
      const d = parseISO(timestamp);
      if (isValid(d)) return d;
    } catch (e) {
      const d = new Date(timestamp);
      if (isValid(d)) return d;
    }
  }
  return null;
};

export function useActivities(tripId: string | undefined) {
  const [groupedActivities, setGroupedActivities] = useState<DayItinerary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!tripId) {
      setGroupedActivities([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const activitiesRef = collection(firestore, 'trips', tripId, 'activities');
    const q = query(activitiesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: ItineraryItem[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ItineraryItem));
      console.log(`Activities loaded for trip ${tripId}:`, items.length, 'activities');
      console.log('Activity details:', items);
      const grouped = groupActivitiesByDate(items);
      console.log('Grouped activities:', grouped);
      setGroupedActivities(grouped);
      setIsLoading(false);
    }, (error) => {
      console.error(`Error loading activities for trip ${tripId}:`, error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [tripId]);

  const groupActivitiesByDate = (items: ItineraryItem[]): DayItinerary[] => {
    const grouped = new Map<string, ItineraryItem[]>();

    items.forEach((activity) => {
      let activityDate: Date | null = null;

      // Check for date field first (used by email imports)
      if (activity.date) {
        activityDate = toDate(activity.date);
      } else if (activity.type === 'hotel' && activity.hotelDetails?.check_in_date) {
        activityDate = toDate(activity.hotelDetails.check_in_date);
      } else if (activity.type === 'flight' && activity.flightDetails?.departureTime) {
        activityDate = toDate(activity.flightDetails.departureTime);
      } else if (activity.time) {
        activityDate = toDate(activity.time);
      }

      const dateKey = activityDate ? format(activityDate, 'yyyy-MM-dd') : 'TBD';
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(activity);
    });

    return Array.from(grouped.entries())
      .map(([dateStr, activities]) => ({
        date: dateStr === 'TBD' ? new Date(9999, 11, 31) : toDate(dateStr) || new Date(),
        activities: activities.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  return { groupedActivities, isLoading };
}